import { Box, CircularProgress, Snackbar, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { useEffect, useRef, useState } from 'react'
import { colors } from 'constants/theme'
import { getWacomStu430, isWebHidSupported } from './wacomStu430'

type Phase = 'connecting' | 'connected'

/**
 * Mount once at the app root. Pops a compact toast only on physical
 * plug-in events (a `'connecting'` from the service immediately followed
 * by `'connect'`). Styled to match Cliniter's medical-formal palette —
 * white card, navy/success accent, Sarabun typography.
 */
export default function WacomConnectionToast() {
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<Phase>('connecting')
  const [text, setText] = useState('')

  const sawConnecting = useRef(false)
  const stuckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isWebHidSupported()) return
    const svc = getWacomStu430()

    const clearStuck = () => {
      if (stuckTimer.current) {
        clearTimeout(stuckTimer.current)
        stuckTimer.current = null
      }
    }

    const off = svc.onConnection((state) => {
      if (state === 'connecting') {
        sawConnecting.current = true
        setPhase('connecting')
        setText('กำลังเชื่อมต่ออุปกรณ์ลงนาม')
        setOpen(true)
        clearStuck()
        stuckTimer.current = setTimeout(() => setOpen(false), 6000)
        return
      }
      if (state === 'connect') {
        clearStuck()
        if (!sawConnecting.current) return
        sawConnecting.current = false
        const name = svc.info?.deviceName || 'อุปกรณ์ลงนาม'
        setPhase('connected')
        setText(`${name} · พร้อมใช้งาน`)
        setOpen(true)
        return
      }
      sawConnecting.current = false
      clearStuck()
      setOpen(false)
    })
    return () => {
      off()
      clearStuck()
    }
  }, [])

  const accent = phase === 'connected' ? colors.statusSuccess : colors.themeMainColor

  return (
    <Snackbar
      open={open}
      autoHideDuration={phase === 'connected' ? 2500 : null}
      onClose={(_e, reason) => {
        if (reason === 'clickaway') return
        setOpen(false)
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pl: 1.5,
          pr: 2,
          py: 1,
          minWidth: 240,
          bgcolor: '#ffffff',
          borderRadius: '4px',
          border: `1px solid ${colors.extraLightGray || 'rgba(0,0,0,0.08)'}`,
          borderLeft: `3px solid ${accent}`,
          boxShadow: '0 4px 16px rgba(47, 66, 105, 0.12), 0 1px 4px rgba(47, 66, 105, 0.06)',
          fontFamily: 'Sarabun, sans-serif',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            flexShrink: 0,
          }}
        >
          {phase === 'connecting' ? (
            <CircularProgress size={18} thickness={4.5} sx={{ color: accent }} />
          ) : (
            <CheckCircleRoundedIcon sx={{ color: accent, fontSize: 20 }} />
          )}
        </Box>
        <Typography
          sx={{
            fontFamily: 'Sarabun, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            color: colors.themeMainColor,
            letterSpacing: 0,
            lineHeight: 1.4,
          }}
        >
          {text}
        </Typography>
      </Box>
    </Snackbar>
  )
}
